import type { Contador, Suscripcion } from '../../api/operaciones';
import type { ProductoCatalogo } from '../../api/ops/plan';
import type { NombreIcono } from '../../componentes/iconos';
import { dia, dinero, miles, nombreBloque, type Bloque, type Dato, type Dinero } from './logica';
import { codigoDeTamano, contratado, TAMANOS } from './recarga';

/**
 * Elegir lo que se contrata (1.15, `1.15-1.17-plan-consumo-pausa-baja.html`): la elección de la prueba antes de contratar
 * («eliges al contratar»), «Añade a tu plan», la ficha del bloque (Qué incluye · Requisitos · Precio) y «Comprar más» (1.16).
 * Lógica sin React. Nombres, precios, cupos y lo que necesita cada bloque salen del catálogo público (`getCatalog`, G1415);
 * sin catálogo, el código legible y sin precio, sin inventar cifras.
 */
export type Catalogo = readonly ProductoCatalogo[] | null;

/** Lo que va en la base o se elige en Negocios (G170): nunca se elige ni se quita aquí. */
const DE_NEGOCIO = ['base', 'reservas', 'citas'];
/** Productos de «Comprar más» (G1004): no son bloques de «Añade a tu plan». */
const DE_COMPRAR_MAS = ['conversaciones_extra', 'envios_extra'];

export const productoDe = (catalogo: Catalogo, codigo: string): ProductoCatalogo | null => catalogo?.find((p) => p.code === codigo) ?? null;

/** El nombre del catálogo; sin él, el código legible. */
export function nombreDe(catalogo: Catalogo, codigo: string): string {
    const p = productoDe(catalogo, codigo);
    return p === null || p.name === '' ? nombreBloque(codigo) : p.name;
}

/** La cuota mensual del catálogo (`price` con `interval: month`); null si no hay o no es mensual. */
export function cuotaDe(catalogo: Catalogo, codigo: string): Dinero | null {
    const precio = productoDe(catalogo, codigo)?.price;
    return precio?.interval === 'month' ? precio.unit : null;
}

const sumar = (a: Dinero, b: Dinero, veces = 1): Dinero => ({ amount: a.amount + b.amount * veces, currency: a.currency });

/** «19 € al mes», o «Sin cuota» con 0. */
export const textoCuota = (m: Dinero | null): string | null => (m === null ? null : m.amount === 0 ? 'Sin cuota' : `${dinero(m)} al mes`);

const vigente = (b: Bloque) => b.ends_at === null || b.ends_at === undefined;

/** Un bloque contratable de «Añade a tu plan» o de la elección de la prueba. */
const esBloque = (p: ProductoCatalogo) =>
    (p.kind === 'block' || p.kind === 'quantity') && p.included_in_base !== true && !DE_NEGOCIO.includes(p.code) && !DE_COMPRAR_MAS.includes(p.code);

/** «Añade a tu plan»: los bloques disponibles del catálogo que el negocio no tiene. */
export function contratables(catalogo: Catalogo, bloques: readonly Bloque[]): ProductoCatalogo[] {
    const tiene = new Set(bloques.map((b) => b.product_code));
    return (catalogo ?? []).filter((p) => esBloque(p) && p.status === 'available' && !tiene.has(p.code));
}

// ---- La prueba: elegir antes de contratar ----

/** Lo elegido para la página de pago: código → unidades. */
export type Eleccion = Readonly<Record<string, number>>;

export interface Elegible {
    codigo: string;
    nombre: string;
    cuota: Dinero | null;
    porUnidades: boolean;
}

/**
 * Lo que se puede elegir al contratar: los bloques de la prueba o de lo contratado (una vuelta tras la baja) y los
 * disponibles del catálogo. Nunca la base (va siempre), Reservas y Citas (Negocios) ni lo incluido.
 */
export function elegibles(catalogo: Catalogo, bloques: readonly Bloque[]): Elegible[] {
    const codigos = new Set<string>();
    for (const b of bloques) {
        if ((b.source === 'trial' || b.source === 'subscription') && !DE_NEGOCIO.includes(b.product_code) && productoDe(catalogo, b.product_code)?.included_in_base !== true) {
            codigos.add(b.product_code);
        }
    }
    for (const p of catalogo ?? []) {
        if (esBloque(p) && p.status === 'available') {
            codigos.add(p.code);
        }
    }
    return [...codigos]
        .sort((a, b) => nombreDe(catalogo, a).localeCompare(nombreDe(catalogo, b), 'es'))
        .map((codigo) => ({ codigo, nombre: nombreDe(catalogo, codigo), cuota: cuotaDe(catalogo, codigo), porUnidades: productoDe(catalogo, codigo)?.kind === 'quantity' }));
}

/** Al abrir «Contratar»: lo que tiene en la prueba (o tenía contratado), con sus unidades, como hasta ahora. */
export function eleccionInicial(catalogo: Catalogo, bloques: readonly Bloque[]): Eleccion {
    const posibles = new Set(elegibles(catalogo, bloques).map((e) => e.codigo));
    return Object.fromEntries(bloques.filter((b) => posibles.has(b.product_code) && vigente(b)).map((b) => [b.product_code, Math.max(1, b.quantity)]));
}

const necesita = (catalogo: Catalogo, codigo: string): string[] => productoDe(catalogo, codigo)?.requires ?? [];

/**
 * Marcar o desmarcar un bloque respetando lo que necesita cada uno (plan de contratación §6; el back lo comprueba igual):
 * al marcar, también lo que necesita y se puede elegir; al desmarcar, también lo elegido que depende de él.
 */
export function cambiarEleccion(eleccion: Eleccion, codigo: string, marcar: boolean, catalogo: Catalogo, posibles: readonly string[]): Eleccion {
    const nueva: Record<string, number> = { ...eleccion };
    if (marcar) {
        const pendientes = [codigo];
        while (pendientes.length > 0) {
            const c = pendientes.pop() ?? '';
            if (posibles.includes(c) && nueva[c] === undefined) {
                nueva[c] = 1;
                pendientes.push(...necesita(catalogo, c));
            }
        }
        return nueva;
    }
    const quitar = [codigo];
    while (quitar.length > 0) {
        const c = quitar.pop() ?? '';
        if (nueva[c] !== undefined) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- la elección es un registro por código
            delete nueva[c];
            quitar.push(...Object.keys(nueva).filter((otro) => necesita(catalogo, otro).includes(c)));
        }
    }
    return nueva;
}

/** Lo que va a la página de pago (`createSubscriptionCheckout`): la base y solo lo elegido. */
export const lineasDePago = (eleccion: Eleccion) => [{ product_code: 'base', quantity: 1 }, ...Object.entries(eleccion).map(([product_code, quantity]) => ({ product_code, quantity }))];

/**
 * Las líneas de la ventana «Contratar el plan» (Base · N bloques · Total al mes). Sin el precio de la base o de algún
 * elegido en el catálogo, null: no se dan cifras a medias.
 */
export function resumenDePago(eleccion: Eleccion, catalogo: Catalogo): { base: Dinero; bloques: Dinero; total: Dinero; n: number } | null {
    const base = cuotaDe(catalogo, 'base');
    if (base === null) {
        return null;
    }
    let bloques: Dinero = { amount: 0, currency: base.currency };
    for (const [codigo, unidades] of Object.entries(eleccion)) {
        const cuota = cuotaDe(catalogo, codigo);
        if (cuota === null) {
            return null;
        }
        bloques = sumar(bloques, cuota, unidades);
    }
    return { base, bloques, total: sumar(base, bloques), n: Object.keys(eleccion).length };
}

export const nBloques = (n: number) => `${String(n)} ${n === 1 ? 'bloque' : 'bloques'}`;

/** «Si contratas hoy» de la prueba (y de la prueba caducada) o «Si vuelves» tras la baja, con lo que se elegiría hoy. */
export function siContratas(s: Suscripcion, eleccion: Eleccion, catalogo: Catalogo): Dato | null {
    const r = resumenDePago(eleccion, catalogo);
    if (r === null || !['trialing', 'trial_expired', 'cancelled'].includes(s.status)) {
        return null;
    }
    const baseYBloques = r.n === 0 ? 'Base' : `Base y ${nBloques(r.n)}`;
    return {
        clave: s.status === 'cancelled' ? 'Si vuelves' : 'Si contratas hoy',
        valor: `${dinero(r.total)} al mes`,
        sub: s.status === 'trialing' ? `${baseYBloques}; eliges al contratar` : baseYBloques,
    };
}

// ---- Con el plan contratado: añadir, quitar y unidades ----

/** Añadir o quitar bloques solo con la suscripción Activa o Impagada (G1072). */
export const admiteCambios = (s: Suscripcion): boolean => s.status === 'active' || s.status === 'past_due';

export type AccionFicha = { tipo: 'anadir' } | { tipo: 'quitar' } | { tipo: 'unidades' } | null;

/**
 * Lo que ofrece la ficha de un bloque al propietario con el plan contratado: añadirlo si no lo tiene (y está disponible),
 * cambiar las unidades de uno por cantidad, o darlo de baja si es contratado (o migrado) y no se ha pedido ya.
 */
export function accionDeFicha(codigo: string, s: Suscripcion, bloques: readonly Bloque[], catalogo: Catalogo): AccionFicha {
    if (DE_NEGOCIO.includes(codigo) || !admiteCambios(s)) {
        return null;
    }
    const b = bloques.find((x) => x.product_code === codigo);
    const p = productoDe(catalogo, codigo);
    if (b === undefined) {
        return p !== null && esBloque(p) && p.status === 'available' ? { tipo: 'anadir' } : null;
    }
    if (!vigente(b) || (b.source !== 'subscription' && b.source !== 'migration')) {
        return null;
    }
    return p?.kind === 'quantity' ? { tipo: 'unidades' } : { tipo: 'quitar' };
}

/** «Añadir a mi plan · 19 € al mes» (ficha) y «Contratar · 19 € al mes» (ventana); sin cuota, «Añadir a mi plan» y «Activar». */
export function textosAnadir(nombre: string, cuota: Dinero | null, s: Pick<Suscripcion, 'next_charge' | 'coupon_code'> | null, metodo: string | null) {
    const conCuota = cuota !== null && cuota.amount > 0;
    const actual = s?.next_charge?.amount ?? null;
    // Con un cupón no se sabe aquí cuánto descuenta (`coupon_code` sin porcentaje): sin «Nueva cuota» antes que inventarla.
    const nueva = actual !== null && cuota !== null && (s?.coupon_code === null || s?.coupon_code === undefined) ? sumar(actual, cuota) : null;
    return {
        ficha: conCuota ? `Añadir a mi plan · ${dinero(cuota)} al mes` : 'Añadir a mi plan',
        titulo: `Añadir ${nombre} a mi plan`,
        texto: `Se activa al momento y se suma a tu cuota desde hoy. Se cobra con ${metodo ?? 'tu método de pago guardado'}.`,
        accion: conCuota ? `Contratar · ${dinero(cuota)} al mes` : 'Activar',
        accionMovil: conCuota ? 'Contratar' : 'Activar',
        lineas: [
            ...(actual === null ? [] : [{ clave: 'Cuota actual', valor: `${dinero(actual)} al mes` }]),
            { clave: nombre, valor: conCuota ? `+ ${dinero(cuota)}` : 'Sin cuota' },
            ...(nueva === null ? [] : [{ clave: 'Nueva cuota', valor: `${dinero(nueva)} al mes` }]),
        ],
        hecho: { titulo: `${nombre} contratado`, texto: 'Ya lo tienes en el panel. Se suma a tu próxima factura.' },
    };
}

/** «¿Dar de baja…?» (G1039): sigue activo hasta el final del periodo pagado; con compromisos vivos, la lista (G1000). */
export function textosQuitarBloque(nombre: string, finPeriodo: string | null, conCompromisos: boolean) {
    const hasta = finPeriodo === null ? 'el final del periodo ya pagado' : `el ${dia(finPeriodo)}, el final del periodo ya pagado`;
    return {
        titulo: `¿Dar de baja ${nombre}?`,
        texto: conCompromisos
            ? `Sigue activo hasta ${hasta}, sin devolución. Tienes compromisos vivos: lo ya vendido se respeta y se valida en la puerta.`
            : `Sigue activo hasta ${hasta}, sin devolución. Después se apaga en el panel, en la web y en el chat.`,
        hecho: (fin: string | null) => ({
            titulo: `${nombre}: baja pedida`,
            texto: `Sigue activo hasta ${fin === null ? 'el final del periodo ya pagado' : `el ${dia(fin)}`} y ese día se retira. Lo que configuraste se guarda por si vuelves.`,
        }),
    };
}

/** «Se retira el 31 de octubre; hasta entonces funciona igual.» de un bloque con la baja pedida (`ends_at`, G1039). */
export const textoSeRetira = (b: Bloque): string | null => (vigente(b) || b.ends_at == null ? null : `Se retira el ${dia(b.ends_at)}; hasta entonces funciona igual.`);

// ---- Ficha del bloque: Qué incluye · Requisitos · Precio ----

const UNIDAD: Readonly<Record<string, string>> = {
    conversations: 'conversaciones',
    sends: 'envíos',
    voice_minutes: 'minutos de voz',
    signatures: 'firmas',
    whatsapp: 'mensajes de WhatsApp',
    whatsapp_transactional: 'mensajes de WhatsApp',
    whatsapp_chatbot: 'mensajes de WhatsApp',
    whatsapp_marketing: 'mensajes de marketing de WhatsApp',
};

export interface LineaFicha {
    texto: string;
    valor?: string;
    icono?: NombreIcono;
    rojo?: boolean;
}

/** Qué incluye: los cupos del catálogo («1.000 conversaciones al mes»). El catálogo aún no tiene la lista de funciones. */
export function queIncluye(p: ProductoCatalogo | null): LineaFicha[] {
    if (p === null) {
        return [];
    }
    const lineas: LineaFicha[] = [];
    if (p.quota != null && p.quota.included !== undefined && p.quota.included > 0 && p.quota.concept !== undefined) {
        const unidad = UNIDAD[p.quota.concept];
        lineas.push({ texto: unidad === undefined ? `${miles(p.quota.included)} al mes` : `${miles(p.quota.included)} ${unidad} al mes`, icono: 'check' });
    }
    if (p.whatsapp_included !== undefined && p.whatsapp_included > 0) {
        lineas.push({ texto: `${miles(p.whatsapp_included)} mensajes de WhatsApp al mes`, icono: 'check' });
    }
    if (p.whatsapp_marketing_included !== undefined && p.whatsapp_marketing_included > 0) {
        lineas.push({ texto: `${miles(p.whatsapp_marketing_included)} mensajes de marketing de WhatsApp al mes`, icono: 'check' });
    }
    return lineas;
}

/** Requisitos: lo que necesita, con «Lo tienes» o «Falta»; sin nada, «No necesita nada más». */
export function requisitos(p: ProductoCatalogo | null, catalogo: Catalogo, bloques: readonly Bloque[]): LineaFicha[] {
    const lista = p?.requires ?? [];
    if (lista.length === 0) {
        return [{ texto: 'No necesita nada más', icono: 'circle-check' }];
    }
    return lista.map((codigo) => {
        const tiene = bloques.some((b) => b.product_code === codigo && vigente(b));
        return { texto: nombreDe(catalogo, codigo), valor: tiene ? 'Lo tienes' : 'Falta', icono: tiene ? 'circle-check' : 'circle-alert', rojo: !tiene };
    });
}

/** Precio: la cuota, la comisión de Entradas (`buyer_fee`) y «Permanencia · Ninguna». */
export function precioDeFicha(codigo: string, catalogo: Catalogo, unidades: number | null): LineaFicha[] {
    const p = productoDe(catalogo, codigo);
    const lineas: LineaFicha[] = [{ texto: 'Cuota', valor: textoCuota(cuotaDe(catalogo, codigo)) ?? (p?.status === 'coming_soon' ? 'Por anunciar' : 'Sin cuota') }];
    if (p?.buyer_fee != null && p.buyer_fee.fixed !== undefined) {
        const pct = p.buyer_fee.percent_bp === undefined ? '' : ` + ${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 }).format(p.buyer_fee.percent_bp / 100)} %`;
        lineas.push({ texto: 'Comisión por entrada', valor: `${dinero(p.buyer_fee.fixed)}${pct}` });
    }
    if (unidades !== null) {
        lineas.push({ texto: 'Tienes', valor: String(unidades) });
    }
    if (codigo !== 'base' && p?.status !== 'coming_soon') {
        lineas.push({ texto: 'Permanencia', valor: 'Ninguna' });
    }
    return lineas;
}

// ---- «Comprar más» (1.16) ----

export type TipoCompra = 'ia' | 'envios' | 'servicio' | 'chatbot' | 'marketing';

interface DefinicionCompra {
    nombre: string;
    /** El bloque que hace falta para ofrecerlo (el diseño: los envíos, solo con Mensajes y campañas). */
    requiere?: string;
    tamanos: readonly { codigo: string; etiqueta: (p: ProductoCatalogo | null) => string }[];
    /** El tamaño que sale elegido al abrir (el diseño: 2.000 en los paquetes). */
    inicial: number;
    texto: (hasta: string | null) => string;
}

const deCupo = (unidad: string, porDefecto: number) => (p: ProductoCatalogo | null) => `${miles(p?.quota?.included ?? porDefecto)} ${unidad}`;
const paquete = (pack: 'transactional' | 'chatbot' | 'marketing') => TAMANOS.map((t) => ({ codigo: codigoDeTamano(pack, t), etiqueta: () => `${miles(t)} mensajes` }));

/** Textos del diseño (PACKS de 1.16); los tamaños y los precios, del catálogo. */
export const COMPRAS: Readonly<Record<TipoCompra, DefinicionCompra>> = {
    ia: {
        nombre: 'Conversaciones del asistente',
        tamanos: [{ codigo: 'conversaciones_extra', etiqueta: deCupo('conversaciones', 1000) }],
        inicial: 0,
        texto: () => 'Un bloque más de 1.000 conversaciones del asistente, solo para este mes: lo que no gastes no pasa al siguiente.',
    },
    envios: {
        nombre: 'Envíos de campañas',
        requiere: 'mensajes_campanas',
        tamanos: [{ codigo: 'envios_extra', etiqueta: deCupo('envíos', 2000) }],
        inicial: 0,
        texto: (hasta) =>
            `Envíos extra solo para este mes en campañas y mensajes automáticos por WhatsApp y Telegram. El email no cuenta. Se suman a los 2.000 del bloque hasta ${hasta === null ? 'final de mes' : `el ${dia(hasta)}`}; lo que no gastes no pasa al mes siguiente.`,
    },
    servicio: {
        nombre: 'Paquete normal · Servicio',
        tamanos: paquete('transactional'),
        inicial: 1,
        texto: () => 'Mensajes normales de WhatsApp: confirmaciones, entradas, pases, avisos y códigos. Se gastan después de los incluidos en tu plan. Prepago: no caducan con el mes.',
    },
    chatbot: {
        nombre: 'Paquete normal · Chatbot',
        tamanos: paquete('chatbot'),
        inicial: 1,
        texto: () => 'Mensajes normales de WhatsApp: respuestas del asistente y del buzón, con el coste de la IA incluido. Se gastan después de los incluidos en tu plan. Prepago.',
    },
    marketing: {
        nombre: 'Paquete Marketing',
        tamanos: paquete('marketing'),
        inicial: 1,
        texto: () => 'Mensajes de marketing de WhatsApp (campañas). Se gastan después de los de marketing incluidos en tu plan. Prepago: no caducan con el mes.',
    },
};

const ORDEN_COMPRAS: readonly TipoCompra[] = ['ia', 'envios', 'servicio', 'chatbot', 'marketing'];

/** Lo que se puede ampliar: todo menos los envíos sin el bloque Mensajes y campañas (como el diseño). */
export function tiposDeCompra(bloques: readonly Bloque[]): TipoCompra[] {
    return ORDEN_COMPRAS.filter((t) => {
        const r = COMPRAS[t].requiere;
        return r === undefined || bloques.some((b) => b.product_code === r);
    });
}

/** `?comprar=` (otras pantallas que abren «Comprar más» con un paquete): su tipo, o null. */
export function tipoDeConsulta(valor: string | null): TipoCompra | null {
    const t = ({ marketing: 'marketing', servicio: 'servicio', whatsapp: 'servicio', chatbot: 'chatbot', ia: 'ia', conversaciones: 'ia', envios: 'envios' } as const)[valor ?? ''];
    return t ?? null;
}

/** La tarjeta de Consumo en alerta que es entera «Comprar más ›» (§21 regla 6): qué abre cada contador. */
export const COMPRA_DE_CONTADOR: Readonly<Record<string, TipoCompra>> = { conversations: 'ia', whatsapp: 'servicio', whatsapp_marketing_plan: 'marketing', sends: 'envios' };

/** Con `faltan` (lo que se necesita), el tamaño más pequeño que lo cubre; si ninguno, el mayor. Sin él, el de por defecto. */
export function tamanoInicial(tipo: TipoCompra, faltan: number | null): number {
    const d = COMPRAS[tipo];
    if (faltan === null || faltan <= 0) {
        return d.inicial;
    }
    const n = d.tamanos.findIndex((t) => Number(/_(\d+)$/.exec(t.codigo)?.[1] ?? '0') >= faltan);
    return n < 0 ? d.tamanos.length - 1 : n;
}

/** El fin del mes de consumo (para «hasta el 31 de octubre» de los envíos). */
export const finDelMes = (contadores: readonly Contador[]): string | null => contadores.find((c) => c.concept === 'sends')?.period_end ?? contadores[0]?.period_end ?? null;

export function textoCompraHecha(tipo: TipoCompra, etiqueta: string): string {
    return tipo === 'envios'
        ? `Ya tienes ${etiqueta} más este mes. Las campañas pausadas por falta de envíos siguen solas. La factura está en Facturas.`
        : 'Ya lo tienes disponible. La factura está en Facturas.';
}

/** «Comprar más» pide el plan contratado (el back, `409` sin él) y no se permite en solo lectura (`x-read-only-mode: blocked`). */
export const puedeComprar = (s: Suscripcion): boolean => s.mode === 'operational' && contratado(s);
